import { useState } from "react";
import { toast } from "react-toastify";
import { initialClientState } from "./CreateNewForm/Clients/ClientData";
import { initialVendorState } from "./CreateNewForm/Vendor/VendorData";
import { sendEntityFormDataAction } from "../../actions/createEntityFormDataActions";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { URL_CONFIG } from "../../config/urlConfig";

export const useFormsData = () => {
    const [activeTab, setActiveTab] = useState("client");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [clientFormData, setClientFormData] = useState(initialClientState);
    const [vendorFormData, setVendorFormData] = useState(initialVendorState);

    const [clientErrors, setClientErrors] = useState({});
    const [vendorErrors, setVendorErrors] = useState({});

    // Single field update — functional updater avoids stale closure
    const handleClientChange = (name, value) => {
        setClientFormData(prev => ({ ...prev, [name]: value }));
        if (clientErrors[name]) {
            setClientErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    // Batch update — used for pincode auto-fill (city + State in one render)
    const handleClientChangeMultiple = (fields) => {
        setClientFormData(prev => ({ ...prev, ...fields }));
    };

    const handleClientBlur = () => { };

    const handleVendorChange = (name, value) => {
        setVendorFormData(prev => ({ ...prev, [name]: value }));
        if (vendorErrors[name]) {
            setVendorErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    const handleSubmit = async (data, type) => {
        setIsSubmitting(true);

        // Build the document upload redirect URL
        const baseUrl = URL_CONFIG.BASE_URL;
        const urlParams = new URLSearchParams();
        if (data.entityCategory) urlParams.append("entityCategory", data.entityCategory);
        if (data.entityLOBType) urlParams.append("entityLOBType", data.entityLOBType);
        if (data.selectedlobtypenationality) urlParams.append("selectedlobtypenationality", data.selectedlobtypenationality);
        if (data.entityName) urlParams.append("entityName", data.entityName);
        if (data.isMSMERegistered) urlParams.append("isMSMERegistered", data.isMSMERegistered);
        const documentUploadURL = `${baseUrl}/upload-documents?${urlParams.toString()}`;

        // Build payload exactly matching server schema
        const payload = {
            // Entity category & LOB
            entityCategory: data.entityCategory || "",
            lobType: data.entityLOBType || "",
            lobNationality: data.selectedlobtypenationality || "",

            // Entity information
            entityName: data.entityName || "",
            entityType: data.entityType || type,
            entityAddress: data.address || "",
            pinCode: parseInt(data.pinCode, 10) || 0,
            city: data.city || "",
            state: data.State || "",
            aaDepartmentFunction: data.aaDepartmentFunction || "",
            natureOfBusiness: data.natureOfBusiness || "",
            panNumber: data.panNumber || "",
            companyPAN: data.CompanypanNumber || "",

            // GST
            isGSTRegistered: convertToBoolean(data.isGSTRegistered),
            gstNumber: data.gstNumberPrimary || "",
            gstAddress: "",
            otherGSTNumbers: data.gstNumbersOther || "",

            // MSME
            isRegisteredUnderMSME: convertToBoolean(data.isMSMERegistered),

            // Contact person
            contactPerson: data.contactPersonName || "",
            designation: data.contactPersonDesignation || "",
            mobileNumber: data.contactPersonMobile || "",
            emailAddress: data.contactPersonEmail || "",

            // Third party (not in form yet — default false)
            thirdPartyPayment: false,
            relationWithBeneficiary: "",

            // Bank details
            bankName: data.bankName || "",
            accountNumber: data.accountNumber || "",
            ifscCode: data.ifscCode || "",
            branchNameAndAddress: data.branchName || "",
            beneficiaryName: data.beneficiaryName || "",

            // Email notification
            sendMeEmail: convertToBoolean(data.sendEmailReceipt),

            // Document upload redirect URL
            documentUploadURL,
        };

        // console.log("=== FORM SUBMISSION ===");
        // console.log("Payload:", payload);
        // console.log("======================");

        try {
            const result = await sendEntityFormDataAction(payload);

            if (result?.success) {
                toast.success(
                    `${type === "client" ? "Client" : "Vendor"} created successfully!`,
                    { position: "top-right", autoClose: 3000 }
                );
                // Reset form
                if (type === "client") {
                    setClientFormData(initialClientState);
                    setClientErrors({});
                } else {
                    setVendorFormData(initialVendorState);
                    setVendorErrors({});
                }
            } else {
                toast.error(result?.error || "Failed to create. Please try again.", {
                    position: "top-right",
                    autoClose: 4000,
                });
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.message || "An error occurred while submitting the form", {
                position: "top-right",
                autoClose: 4000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (activeTab === "client") {
            setClientFormData(initialClientState);
            setClientErrors({});
        } else {
            setVendorFormData(initialVendorState);
            setVendorErrors({});
        }
        toast.info("Form cleared", { position: "top-right", autoClose: 2000 });
    };

    const handleTabChange = (tab) => {
        if (!isSubmitting) setActiveTab(tab);
    };

    return {
        activeTab,
        isSubmitting,
        clientFormData,
        vendorFormData,
        clientErrors,
        vendorErrors,
        setClientErrors,
        setVendorErrors,
        handleClientChange,
        handleClientChangeMultiple,
        handleClientBlur,
        handleVendorChange,
        handleSubmit,
        handleCancel,
        handleTabChange,
    };
};
